import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { X, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function AuthModal({ isOpen, onClose }) {
  const { loginWithEmail } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await loginWithEmail(email, password);
      toast.success(t('auth_login_success'));
      onClose();
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || t('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-8" data-testid="auth-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600" data-testid="close-auth-modal">
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-brand-orange rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 512 512"><path d="M226.5 92.9c14.3 42.9-.3 86.2-32.6 96.8s-70.1-15.6-84.4-58.5.3-86.2 32.6-96.8 70.1 15.6 84.4 58.5zM100.4 198.6c18.9 32.4 14.3 70.1-10.2 84.1s-59.7-.9-78.5-33.3S-2.7 179.3 21.8 165.3s59.7.9 78.6 33.3zM69.2 401.2C121.6 259.9 214.7 224 256 224s134.4 35.9 186.8 177.2c3.6 9.7 5.2 20.1 5.2 30.5v1.6c0 25.8-20.9 46.7-46.7 46.7-11.5 0-22.9-1.4-34-4.2l-88-22c-15.3-3.8-31.3-3.8-46.6 0l-88 22c-11.1 2.8-22.5 4.2-34 4.2-25.8 0-46.7-20.9-46.7-46.7v-1.6c0-10.4 1.6-20.8 5.2-30.5zM324.7 89.4c14.3-42.9 51.7-66.1 83.6-51.5 32 14.6 45.9 57.9 31.6 100.7-14.3 42.9-51.7 66.1-83.6 51.5-32-14.6-45.9-57.9-31.6-100.7zm89.1 164.5c24.5 14 29.1 51.7 10.2 84.1-18.9 32.4-54 46.1-78.5 32.1s-29.1-51.7-10.2-84.1c18.9-32.4 54-46.1 78.5-32.1z"/></svg>
          </div>
          <h2 className="text-xl font-heading font-bold text-brand-brown">
            {t('auth_login_title')}
          </h2>
          <p className="text-brand-muted text-sm mt-1">
            {t('auth_login_subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-1">{t('auth_email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field w-full"
              placeholder="hello@petsbox.eu"
              required
              data-testid="auth-email"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-1">{t('auth_password')}</label>
            <div className="relative">
              <input
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field w-full pr-10"
                required
                data-testid="auth-password"
              />
              <button type="button" onClick={() => setShowPass(!showPass)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {error && <p className="text-red-500 text-sm" data-testid="auth-error">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand-orange text-white py-3 rounded-full font-semibold hover:bg-brand-orange-hover transition-colors disabled:opacity-50"
            data-testid="auth-submit"
          >
            {loading ? '...' : t('auth_login_btn')}
          </button>
        </form>

        <p className="text-center text-xs text-brand-muted mt-4">
          {t('auth_login_subtitle')}
        </p>
      </div>
    </div>
  );
}
